import { Component, inject, Inject } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ParticipanteService } from '../../../../services/participante.service';
import { ToastService, SnackbarStyles } from '../../../../commons/toast.service';
import { finalize, Subscription } from 'rxjs';
import { A11yModule } from "@angular/cdk/a11y";

@Component({
  selector: 'app-dlg-lista-envio',
  templateUrl: './dlg-lista-envio.component.html',
  styleUrls: ['./dlg-lista-envio.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatListModule, A11yModule]
})
export class DlgListaEnvioComponent {
  dialogRef: MatDialogRef<DlgListaEnvioComponent> = inject(MatDialogRef<DlgListaEnvioComponent>);
  participanteServ: ParticipanteService = inject(ParticipanteService);
  private toast: ToastService = inject(ToastService);
  private data: any = inject(MAT_DIALOG_DATA, { optional: true });

  name: string = '';
  searchText: string = '';
  available: any[] = [];
  selected: any[] = [];

  selectedAvailableItem: any | null = null;
  selectedSelectedItem: any | null = null;

  searching = false;
  private searchSub?: Subscription;

  constructor() {}

  ngOnInit(): void {
    if (this.data) {
      const id = this.data.id ?? this.data.codigo ?? this.data.codigoLista ?? this.data.listaId ?? this.data._id ?? this.data.idLista;
      this.name = this.data.nombre ?? this.data.titulo ?? this.data.tituloLista ?? this.data.nombreLista ?? this.data.name ?? '';
      if (id != null) this.loadSelected(id);
    }
  }

  loadSelected(id: any) {
    const listId = id && id.id ? (id.id) : id;
    this.participanteServ.getParticipanteListaEnvio(listId).subscribe({
      next: (res: any[]) => {
        this.selected = res || [];
        this.available = (this.available || []).filter(a => !this.selected.find(s => s.id === a.id));
      },
        error: (err: any) => {
          this.toast.show({ message: 'No se pudieron cargar los participantes de la lista', style: SnackbarStyles.Warning });
        }
    });
  }

  search() {
    const term = (this.searchText || '').trim();
    const query = term.length >= 2 ? term : undefined;

    this.searchText = term;
    this.selectedAvailableItem = null;

    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }

    this.searching = true;
    this.searchSub = this.participanteServ.getServidoresParaLista(query)
    .pipe(finalize(() => this.searching = false))
    .subscribe({
      next: (res: any[]) => {
        this.available = (res || []).filter(r => !this.selected.find(s => s.id === r.id));
      },
      error: () => {
        this.available = [];
      }
    });
  }

  ngOnDestroy(): void {
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
  }

  add(item: any) {
    if (!item && this.selectedAvailableItem) {
      item = this.selectedAvailableItem;
    }
    if (!item) return;
    this.selected.push(item);
    this.available = this.available.filter(a => a.id !== item.id);
    this.selectedAvailableItem = null;
  }

  remove(item: any) {
    if (!item && this.selectedSelectedItem) {
      item = this.selectedSelectedItem;
    }
    if (!item) return;
    this.selected = this.selected.filter(s => s.id !== item.id);
    this.available.push(item);
    this.selectedSelectedItem = null;
  }

  addAll() {
    const toAdd = this.available.filter(a => !this.selected.find(s => s.id === a.id));
    this.selected = this.selected.concat(toAdd);
    this.available = [];
  }

  removeAll() {
    const toReturn = this.selected.filter(s => !this.available.find(a => a.id === s.id));
    this.available = this.available.concat(toReturn);
    this.selected = [];
  }

  selectAvailable(item: any) {
    this.selectedAvailableItem = this.selectedAvailableItem && this.selectedAvailableItem.id === item.id ? null : item;
    this.selectedSelectedItem = null;
  }

  selectSelected(item: any) {
    this.selectedSelectedItem = this.selectedSelectedItem && this.selectedSelectedItem.id === item.id ? null : item;
    this.selectedAvailableItem = null;
  }

  moveOneRight() {
    if (this.selectedAvailableItem) this.add(this.selectedAvailableItem);
  }

  moveOneLeft() {
    if (this.selectedSelectedItem) this.remove(this.selectedSelectedItem);
  }

  getDisplayName(item: any): string {
    if (!item) return '';
    return (item.nombres || item.nombre || item.titulo || '').toString();
  }

  getInitials(item: any): string {
    const name = this.getDisplayName(item).trim();
    if (!name) return '';
    const parts = name.split(/\s+/).filter(p => p.length);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  accept() {
    if (!this.name || this.name.trim().length === 0) {
      this.toast.show({ message: 'Nombre es requerido', style: SnackbarStyles.Warning });
      return;
    }

    const payload = {
      nombre: this.name.trim(),
      codigosDestinatarios: (this.selected || []).map(s => Number(s.id))
    };

    const makeCloseObj = (res: any) => {
      const nameFromRes = res && typeof res === 'object' ? (res.nombre ?? res.name ?? res.titulo ?? res.nombreLista ?? null) : null;
      const idFromRes = res && typeof res === 'object' ? (res.id ?? res.listaId ?? res._id ?? null) : null;
      if (nameFromRes || idFromRes) return { id: idFromRes, nombre: nameFromRes ?? this.name.trim() };
      return { id: idFromRes ?? null, nombre: this.name.trim() };
    };

    if (this.data && this.data.id) {
      this.participanteServ.editListaEnvio(Number(this.data.id), payload).subscribe({
        next: (res: any) => this.dialogRef.close(makeCloseObj(res)),
        error: (err: any) => {
          this.toast.show({ message: 'Error editando lista de envío', style: SnackbarStyles.Error });
        }
      });
    } else {
      this.participanteServ.createListaEnvio(payload).subscribe({
        next: (res: any) => this.dialogRef.close(makeCloseObj(res)),
        error: (err: any) => {
          this.toast.show({ message: 'Error creando lista de envío', style: SnackbarStyles.Error });
        }
      });
    }
  }

  cancel() {
    this.dialogRef.close();
  }
}
